// Phase SIKA-5e/2 — Claude.ai-style message renderer (v2).
//
// Role-branched single-component replacement for SikaChatMessage:
//   - role === "user"      → right-aligned pill (.sc2-msg-user)
//   - role === "assistant" → full-width prose, no bubble, no avatar
//                            (.sc2-msg-assistant + .sika-prose)
//   - role === "tool" + card_type → inline attachment card with the
//     existing SikaCardRenderer body inside
//   - role === "tool" without card_type → hidden
//
// Streaming UX: when `streaming` is true, the assistant content
// renders a saffron block cursor (▌) after the last token. While
// the assistant is still pending tokens AND content is empty, a
// muted "SIKA is thinking…" line shows instead.
//
// CSS lives in src/styles/super-acca.css under .sika-chat-2. The
// host page must mount that class inside .sa-root for styles to
// apply — see SikaChatPage.v2 (5e/4).

import Markdown from "react-markdown";

import type { ChatMessage } from "../../../sika/useSikaChat.ts";
import {
  SikaAgentDeploy,
  parseAgentSteps,
  type AgentStep,
} from "../SikaAgentDeploy.tsx";
import { SikaCardRenderer } from "../cards/SikaCardRenderer.tsx";
import { InlineCard, describeCardType } from "./InlineCard.tsx";

export interface SikaMessageV2Props {
  msg: ChatMessage;
  /** Forwarded to action-capable cards (ACTIVATE, ADJUST, SAVE). */
  onAction?: (text: string) => void;
  /** True while SIKA is still responding — hides card action buttons. */
  sending?: boolean;
  /** True if this is the in-flight streaming assistant message. */
  streaming?: boolean;
}

function getCardData(msg: ChatMessage): unknown | null {
  if (!msg.card_type) return null;
  const tu = msg.tool_use as { result?: unknown } | null;
  return tu?.result ?? null;
}

export function SikaMessageV2({
  msg,
  onAction,
  sending = false,
  streaming = false,
}: SikaMessageV2Props): JSX.Element | null {
  // ── User pill ────────────────────────────────────────────
  if (msg.role === "user") {
    if (!msg.content?.trim()) return null;
    return (
      <div
        className="sc2-msg-user"
        data-role="user"
        data-testid="sc2-msg-user"
      >
        {msg.content}
      </div>
    );
  }

  // ── Tool with card data ──────────────────────────────────
  if (msg.role === "tool") {
    const cardData = getCardData(msg);
    if (!cardData || !msg.card_type) return null; // hide tool-only messages
    const { iconGlyph, title } = describeCardType(msg.card_type);
    return (
      <div
        className="sc2-msg-assistant"
        data-role="tool-card"
        data-testid="sc2-msg-card"
      >
        <InlineCard
          title={title}
          meta={msg.card_type}
          iconGlyph={iconGlyph}
          testId={`sc2-card-${msg.card_type}`}
        >
          <SikaCardRenderer
            cardType={msg.card_type}
            data={cardData}
            onAction={onAction}
            sending={sending}
          />
        </InlineCard>
      </div>
    );
  }

  // ── Assistant prose ──────────────────────────────────────
  if (msg.role === "assistant") {
    const agentSteps: AgentStep[] | null = parseAgentSteps(msg.tool_use);
    const hasContent = !!msg.content?.trim();
    const hasSteps = (agentSteps?.length ?? 0) > 0;

    // Empty assistant message (tool-use only, no text) → hidden unless
    // we have agent steps OR we're actively streaming.
    if (!hasContent && !hasSteps && !streaming) return null;

    return (
      <div
        className="sc2-msg-assistant"
        data-role="assistant"
        data-testid="sc2-msg-assistant"
      >
        {hasSteps ? (
          <div className="sc2-agent-steps" data-testid="sc2-agent-steps">
            <SikaAgentDeploy steps={agentSteps!} variant="inline" />
          </div>
        ) : null}

        {hasContent ? (
          <div className="sika-prose">
            <Markdown>{msg.content}</Markdown>
            {streaming ? <span className="sc2-cursor" aria-hidden /> : null}
          </div>
        ) : streaming ? (
          <div className="sc2-thinking" data-testid="sc2-thinking">
            SIKA is thinking…
          </div>
        ) : null}
      </div>
    );
  }

  return null;
}
